import React from 'react';
import { useSelector } from 'react-redux';

import { ALERT_SEVERITY_ENUM } from '../enums';
import { useNavigate } from '../libs/react-router';
import { BaseStore } from '../types';
import * as types from '../types';

type IUseResourceById<TResource = any> = {
  item_type: keyof BaseStore['resources'];
  id: number;
  effects?: Array<(_resource: TResource) => Promise<any>>;
  setAlert?: (_alert: types.common.Alert | null) => void;
};

/**
 * Hook that finds a resource by ID from Redux store and manages loading/error states
 *
 * @template TResource Type of the resource being loaded using redux store key
 * @param item_type - Resource type key from redux store
 * @param id - ID of the resource to find
 * @param effects - Optional array of promise-returning functions to execute when resource is loaded
 * @param setAlert - Optional function to set alert messages for errors
 * @returns Object containing resource and loading state
 *
 */
const useResourceById = <TResource = any>({ item_type, id, effects, setAlert }: IUseResourceById<TResource>) => {
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  const resources = useSelector((state: BaseStore) => state.resources[item_type] as TResource[]);
  const storeResource = React.useMemo(
    () => (resources as TResource[]).find((r: any) => r.id == id),
    [resources, id]
  ) as TResource | undefined;

  const loadResource = React.useCallback(async (): Promise<TResource> => {
    return storeResource as TResource;
  }, []);

  React.useEffect(() => {
    const performLoad = async () => {
      try {
        setLoading(true);
        const resource = await loadResource();
        if (!resource || (resource as any)?.revoked) {
          navigate('/404');
          return;
        }

        if (effects) {
          await Promise.all(effects.map((effect) => effect(resource)));
        }
      } catch (err) {
        if (setAlert)
          setAlert({
            severity: ALERT_SEVERITY_ENUM.ERROR,
            message: 'Unable to load resource',
          });
      } finally {
        setLoading(false);
      }
    };

    performLoad();
  }, []);

  return {
    loading,
  };
};

export default useResourceById;
